/**
 * ThemeProvider.jsx
 * -----------------------------------------------------------------------------
 * Provider único de tema para xFlow. Gestiona el estado claro/oscuro y lo aplica
 * al documento (atributo data-theme + tema dark de Syncfusion vía themeManager).
 *
 * Expone:
 *   - useTheme(): { theme, isDark, toggleTheme, setTheme }
 *
 * Se monta en App.js envolviendo toda la app. El modo claro es el comportamiento
 * por defecto y no cambia nada respecto al original.
 *
 * Nota: este proyecto NO tiene @mui instalado (los componentes que lo importan
 * son código no alcanzable desde las rutas). Por eso el provider es agnóstico de
 * MUI; si en el futuro se incorpora MUI, envolver aquí con su ThemeProvider
 * ligando palette.mode a `theme`.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { applyTheme, getInitialTheme, persistTheme } from "./themeManager";

const ThemeContext = createContext({
  theme: "light",
  isDark: false,
  toggleTheme: () => {},
  setTheme: () => {},
});

export function useTheme() {
  return useContext(ThemeContext);
}

export default function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(getInitialTheme);

  // Aplica el tema al documento cada vez que cambia.
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = useCallback((next) => {
    setThemeState((prev) => {
      const value = next === "dark" || next === "light" ? next : prev;
      persistTheme(value);
      return value;
    });
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      const value = prev === "dark" ? "light" : "dark";
      persistTheme(value);
      return value;
    });
  }, []);

  const value = useMemo(
    () => ({
      theme,
      isDark: theme === "dark",
      toggleTheme,
      setTheme,
    }),
    [theme, toggleTheme, setTheme]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}
